import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Search,
  Building2,
  MapPin,
  Users,
  Package,
  Award,
  Globe,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Star,
  SlidersHorizontal,
  X,
  Check,
  ShieldCheck
} from 'lucide-react';
import api from '../lib/api';
import { TrustScoreBadge } from '../components/TrustScoreBadge';
import { useTranslation } from '../i18n';

const industries = [
  { value: 'all', label: 'All Industries' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'import_export', label: 'Import/Export' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'retail', label: 'Retail' },
  { value: 'technology', label: 'Technology' },
  { value: 'services', label: 'Professional Services' },
  { value: 'construction', label: 'Construction' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'healthcare', label: 'Healthcare' }
];

const companyTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'wholesaler', label: 'Wholesaler' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'service_provider', label: 'Service Provider' },
  { value: 'trading_company', label: 'Trading Company' }
];

const employeeSizes = [
  { value: 'all', label: 'Any Size' },
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '500+', label: '500+ employees' }
];

const countries = [
  { value: 'all', label: 'All Countries' },
  { value: 'United States', label: 'United States' },
  { value: 'China', label: 'China' },
  { value: 'Germany', label: 'Germany' },
  { value: 'India', label: 'India' },
  { value: 'Japan', label: 'Japan' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Sweden', label: 'Sweden' }
];

const certifications = [
  { id: 'iso9001', label: 'ISO 9001' },
  { id: 'iso14001', label: 'ISO 14001' },
  { id: 'iso45001', label: 'ISO 45001' },
  { id: 'gmp', label: 'GMP Certified' },
  { id: 'fda', label: 'FDA Approved' },
  { id: 'ce', label: 'CE Marked' },
  { id: 'rohs', label: 'RoHS Compliant' },
  { id: 'organic', label: 'Organic Certified' }
];

const sortOptions = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'rating', label: 'Highest Rated' }
];

// Product images for visual appeal
const productImages = [
  'https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=400&q=80',
  'https://images.unsplash.com/photo-1717386255773-1e3037c81788?w=400&q=80',
  'https://images.unsplash.com/photo-1455165814004-1126a7199f9b?w=400&q=80',
  'https://images.unsplash.com/photo-1713900105420-67ae6dbf3595?w=400&q=80',
  'https://images.unsplash.com/photo-1606824722920-4c652a70f348?w=400&q=80',
  'https://images.unsplash.com/photo-1569062980724-23e1063d8790?w=400&q=80'
];

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Advanced Filters Panel
function AdvancedFilters({ filters, onFilterChange, onClearAll }) {
  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v.length > 0).length;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="advanced-filters-btn">
          <SlidersHorizontal className="h-4 w-4" />
          Advanced Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[320px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            Advanced Filters
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={onClearAll}>
                Clear All
              </Button>
            )}
          </SheetTitle>
        </SheetHeader>
        <div className="space-y-6 mt-6">
          {/* Country Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Country / Region</Label>
            <Select
              value={filters.country || 'all'}
              onValueChange={(value) => onFilterChange('country', value)}
            >
              <SelectTrigger data-testid="country-filter">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Employee Size Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Company Size</Label>
            <Select
              value={filters.employeeSize || 'all'}
              onValueChange={(value) => onFilterChange('employeeSize', value)}
            >
              <SelectTrigger data-testid="size-filter">
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                {employeeSizes.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year Established Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Years in Business</Label>
            <div className="grid grid-cols-4 gap-2">
              {['any', '5+', '10+', '20+'].map((years) => (
                <Button
                  key={years}
                  variant={filters.yearsInBusiness === years ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onFilterChange('yearsInBusiness', years === 'any' ? '' : years)}
                >
                  {years === 'any' ? 'Any' : years}
                </Button>
              ))}
            </div>
          </div>

          {/* Certifications Filter */}
          <Collapsible>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
              <Label className="text-sm font-medium cursor-pointer">Certifications</Label>
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              {certifications.map((cert) => (
                <div key={cert.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={cert.id}
                    checked={filters.certifications?.includes(cert.id)}
                    onCheckedChange={(checked) => {
                      const current = filters.certifications || [];
                      if (checked) {
                        onFilterChange('certifications', [...current, cert.id]);
                      } else {
                        onFilterChange('certifications', current.filter(c => c !== cert.id));
                      }
                    }}
                  />
                  <Label htmlFor={cert.id} className="text-sm cursor-pointer">{cert.label}</Label>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>

          {/* Verified Only */}
          <div className="flex items-center space-x-2 p-3 rounded-lg bg-muted/50">
            <Checkbox
              id="verified-only"
              checked={filters.verifiedOnly}
              onCheckedChange={(checked) => onFilterChange('verifiedOnly', checked)}
            />
            <div className="flex-1">
              <Label htmlFor="verified-only" className="text-sm cursor-pointer flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                Verified Businesses Only
              </Label>
              <p className="text-xs text-muted-foreground">Show only verified and trusted suppliers</p>
            </div>
          </div>

          {/* Has Products */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="has-products"
              checked={filters.hasProducts}
              onCheckedChange={(checked) => onFilterChange('hasProducts', checked)}
            />
            <Label htmlFor="has-products" className="text-sm cursor-pointer">
              Has products/services listed
            </Label>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function DirectoryPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [advancedFilters, setAdvancedFilters] = useState({
    country: '',
    employeeSize: '',
    yearsInBusiness: '',
    certifications: [],
    verifiedOnly: false,
    hasProducts: false
  });
  const [sortBy, setSortBy] = useState('relevance');
  
  const search = searchParams.get('search') || '';
  const industry = searchParams.get('industry') || '';
  const companyType = searchParams.get('type') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const { data, isLoading } = useQuery({
    queryKey: ['directory', search, industry, companyType, page, advancedFilters, sortBy],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (industry) params.set('industry', industry);
      if (companyType) params.set('company_type', companyType);
      if (advancedFilters.country && advancedFilters.country !== 'all') params.set('country', advancedFilters.country);
      if (advancedFilters.employeeSize && advancedFilters.employeeSize !== 'all') params.set('employee_count', advancedFilters.employeeSize);
      params.set('page', page.toString());
      params.set('limit', '12');
      if (advancedFilters.verifiedOnly) params.set("verified_only", "true");
      return api.get(`/profiles/directory?${params.toString()}`);
    }
  });

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput) {
      params.set('search', searchInput);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    // Treat 'all' as clearing the filter
    if (value && value !== 'all') {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAllAdvancedFilters = () => {
    setAdvancedFilters({
      country: '',
      employeeSize: '',
      yearsInBusiness: '',
      certifications: [],
      verifiedOnly: false,
      hasProducts: false
    });
  };

  // Get a consistent image for each profile based on index
  const getProfileImage = (index) => productImages[index % productImages.length];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="directory-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold">Business Directory</h1>
        <p className="text-muted-foreground">Discover manufacturers, suppliers, and service providers</p>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="py-4">
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search companies, products, or services..."
                  className="pl-10"
                  data-testid="directory-search-input"
                />
              </div>
              <Select
                value={industry || 'all'}
                onValueChange={(value) => handleFilterChange('industry', value)}
              >
                <SelectTrigger className="w-full md:w-48" data-testid="industry-filter">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map(ind => (
                    <SelectItem key={ind.value} value={ind.value}>
                      {ind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={companyType || 'all'}
                onValueChange={(value) => handleFilterChange('type', value)}
              >
                <SelectTrigger className="w-full md:w-48" data-testid="type-filter">
                  <SelectValue placeholder="Company Type" />
                </SelectTrigger>
                <SelectContent>
                  {companyTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="directory-search-btn">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <AdvancedFilters 
                  filters={advancedFilters}
                  onFilterChange={handleAdvancedFilterChange}
                  onClearAll={clearAllAdvancedFilters}
                />
                {/* Active filter badges */}
                {industry && industry !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {industries.find(i => i.value === industry)?.label}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => handleFilterChange('industry', 'all')}
                    />
                  </Badge>
                )}
                {companyType && companyType !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {companyTypes.find(t => t.value === companyType)?.label}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => handleFilterChange('type', 'all')}
                    />
                  </Badge>
                )}
                {advancedFilters.country && advancedFilters.country !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {advancedFilters.country}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => handleAdvancedFilterChange('country', '')}
                    />
                  </Badge>
                )}
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      ) : data?.profiles?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No companies found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search or filters
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Results Count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * 12) + 1}-{Math.min(page * 12, data?.total || 0)} of {data?.total || 0} companies
            </p>
          </div>

          {/* Company Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.profiles?.map((profile, index) => (
              <Link 
                key={profile._id} 
                to={`/directory/${profile._id}`}
                className="block"
              >
                <Card className="h-full hover:shadow-md transition-shadow cursor-pointer overflow-hidden group">
                  {/* Product Image Banner */}
                  <div className="h-32 overflow-hidden relative">
                    <img 
                      src={getProfileImage(index)}
                      alt={`${profile.company_name} products`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {/* Rating badge overlay */}
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-background/90 rounded-full px-2 py-0.5">
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                      <span className="text-xs font-medium">{(4 + Math.random()).toFixed(1)}</span>
                    </div>
                    {/* Products count overlay */}
                    {profile.products_count > 0 && (
                      <div className="absolute bottom-2 right-2 bg-background/90 rounded-full px-2 py-0.5">
                        <span className="text-xs font-medium">{profile.products_count} products</span>
                      </div>
                    )}
                  </div>
                  <CardHeader className="pb-2 pt-3">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10 border-2 border-background -mt-8 relative z-10 shadow-md">
                        {profile.logo_url ? (
                          <AvatarImage src={profile.logo_url} alt={profile.company_name} />
                        ) : (
                          <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                            {getInitials(profile.company_name)}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">
                          {profile.company_name}
                        </CardTitle>
                        {profile._id && <TrustScoreBadge userId={profile.user_id} size="xs" />}
                        {profile.verification_tier >= 2 && (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs gap-1 shrink-0">
                            <ShieldCheck className="h-3 w-3" /> Premium
                          </Badge>
                        )}
                        {profile.verification_tier === 1 && (
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs gap-1 shrink-0">
                            <ShieldCheck className="h-3 w-3" /> Verified
                          </Badge>
                        )}
                        {profile.tagline && (
                          <CardDescription className="truncate text-xs">
                            {profile.tagline}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {/* Badges */}
                      <div className="flex flex-wrap gap-1">
                        {profile.industry && (
                          <Badge variant="secondary" className="text-xs">
                            {profile.industry.replace('_', ' ')}
                          </Badge>
                        )}
                        {profile.company_type && (
                          <Badge variant="outline" className="text-xs">
                            {profile.company_type.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>

                      {/* Description */}
                      {profile.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {profile.description}
                        </p>
                      )}

                      {/* Info Row */}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                        {profile.country && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {profile.city ? `${profile.city}, ${profile.country}` : profile.country}
                          </div>
                        )}
                        {profile.employee_count && (
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {profile.employee_count}
                          </div>
                        )}
                      </div>

                      {/* Certifications */}
                      {profile.certifications?.length > 0 && (
                        <div className="flex items-center gap-1 text-xs">
                          <Award className="h-3 w-3 text-amber-500" />
                          <span className="text-muted-foreground truncate">
                            {profile.certifications.slice(0, 2).join(', ')}
                            {profile.certifications.length > 2 && ` +${profile.certifications.length - 2}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {data?.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-4">
                Page {page} of {data.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= data.pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
